import { useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Tooltip } from 'react-tooltip';
import { CURRENCY_USD } from 'constants/currency';
import { COACH_PAYMENT_API, PostRequestPayload } from 'constants/payloads/lessonsBook';
import { LocationType } from 'utils/shared/coachBuilder';
import { convertToISOStrings, getDateOnly } from 'utils/shared/date/addHourToSlot';
import { detectFraudFromBrowser } from 'utils/shared/fraud/detectFraud';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { getLessonItemizedTotal } from 'utils/shared/money/getLessonItemizedTotal';
import { getNavigatorLanguage } from 'utils/shared/time/getNavigatorLanguage';
import { getTimezone } from 'utils/shared/time/getTimezone';
import { toTitleCase } from 'utils/shared/toTitleCase';
import { useApiGateway } from 'hooks/useApi';
import InfoCircle from 'svg/InfoCircle';
import { ConfirmAndPayProps, PriceRowProps } from 'screens/PlayerLoggedInScreen/props';
import Button from 'components/Button';
import useIsFollowingCoach from 'components/Coach/useIsFollowingCoach';
import ManagePaymentCards from 'components/Premium/ManagePaymentCards';
import classNames from 'styles/utils/classNames';
import BookedLessonCard from './BookedLessonCard';

const ConfirmAndPay: React.FC<ConfirmAndPayProps> = ({ bookingData, coach, onNext }) => {
  const paymentFormRef = useRef<any>();

  const coachPrice = coach.priceUnitAmountCoachDefault ?? 0;

  const timezoneName = getTimezone();
  const timezoneOffsetMinutes = Math.round(bookingData.dateFromCalendar!.getTimezoneOffset());
  const timezoneAbbreviation = bookingData
    .dateFromCalendar!.toLocaleString('en', { timeZoneName: 'short' })
    .split(' ')
    .pop();

  const { isFollowing } = useIsFollowingCoach({ coachId: coach?.id });

  const includeCoachIntroFee = !isFollowing;

  const {
    post: createCoachPayment,
    isError: paymentError,
    error: paymentErrorMessage,
    isLoading,
  } = useApiGateway<PostRequestPayload>(COACH_PAYMENT_API);

  const prices = getLessonItemizedTotal({
    priceUnitAmount: coachPrice,
    includeCoachIntroFee,
  });

  const handleSubmitPayment = async (paymentMethodId: string) => {
    // Check for fraud before proceeding with payment
    const fraudDetection = detectFraudFromBrowser();
    if (fraudDetection.isFraud) {
      Sentry.captureException(new Error('Fraud attempt detected during lesson booking'), {
        extra: {
          reason: fraudDetection.reason,
          timezone: fraudDetection.timezone,
          locale: fraudDetection.locale,
          coachId: coach.id,
          amount: prices?.priceUnitAmount,
        },
      });
      throw new Error('Unable to process booking. Please try again later.');
    }

    const conversionData = convertToISOStrings(
      bookingData.currentDateTime?.date ? getDateOnly(bookingData.currentDateTime?.date) : '',
      bookingData.currentDateTime?.startTime || '',
      bookingData.currentDateTime?.endTime || '',
    );

    const response = await createCoachPayment({
      payload: {
        coachId: coach.id,
        paymentMethodId: paymentMethodId,
        amount: prices?.priceUnitAmount,
        date: bookingData.currentDateTime?.date
          ? getDateOnly(bookingData.currentDateTime?.date)
          : '',
        startTime: conversionData?.startDateTime,
        endTime: conversionData?.endDateTime,
        locale: getNavigatorLanguage(),
        timezoneName: timezoneName,
        timezoneAbbreviation: timezoneAbbreviation || '',
        timezoneOffsetMinutes: timezoneOffsetMinutes || 0,
        venueId:
          bookingData.location?.locationType === LocationType?.Venue
            ? bookingData.location?.id
            : '',
        userCustomCourtId:
          bookingData.location?.locationType === LocationType?.Custom
            ? bookingData.location?.id
            : '',
        fullAddress: bookingData.location?.address,
        clientTimezone: fraudDetection.timezone,
        clientLocale: fraudDetection.locale,
      },
    });

    if (response.data) {
      onNext({ bookedLessonId: response.data.lesson.id });
    }

    if (response?.isError) {
      throw response?.error;
    }
  };

  return (
    <div className="px-ds-xl pt-ds-xl max-sm:px-ds-lg">
      <BookedLessonCard
        sport={coach.defaultSport as string}
        coachName={toTitleCase(coach.fullName || '')}
        image={coach.profileImageProviderUrl || ''}
        location={bookingData.location.address}
        time={`${bookingData.currentDateTime?.date.toDateString()} at ${
          bookingData?.currentDateTime.startTime
        }`}
      />
      <div className="mt-6 flex flex-col gap-4 max-sm:gap-ds-md">
        <PriceRow
          title="Lesson"
          tooltip={null}
          amount={
            convertUnitPriceToFormattedPrice(
              prices?.orderSubtotal,
              coach?.stripeMerchantCurrency || coach?.currencyCoachDefault || CURRENCY_USD,
            ).priceDisplay
          }
        />
        {!!prices.coachAmountReceived && (
          <PriceRow
            tooltip={null}
            title="Taxes and credit card fees"
            amount={
              convertUnitPriceToFormattedPrice(
                prices?.creditCardFee,
                coach?.stripeMerchantCurrency || coach?.currencyCoachDefault || CURRENCY_USD,
              ).priceDisplayFull
            }
          />
        )}
        {!!prices.coachIntroFeeCents && (
          <PriceRow
            tooltip={{
              id: 'coach-intro-fee',
              text: 'The is a one time fee for players booking a lesson with a new coach',
            }}
            title="Intro Fee"
            amount={
              convertUnitPriceToFormattedPrice(
                prices?.coachIntroFeeCents,
                coach?.stripeMerchantCurrency || coach?.currencyCoachDefault || CURRENCY_USD,
              ).priceDisplayFull
            }
          />
        )}
        <PriceRow
          tooltip={null}
          brand
          title="Total"
          amount={
            convertUnitPriceToFormattedPrice(
              prices?.orderTotal,
              coach?.stripeMerchantCurrency || coach?.currencyCoachDefault || CURRENCY_USD,
            ).priceDisplay
          }
        />
      </div>
      <hr className="my-6" />
      <span className="typography-product-subheading mb-4 block">Select credit card</span>
      <ManagePaymentCards
        hideSubmitBtn
        ref={paymentFormRef}
        isLoading={isLoading}
        handleSubmitPayment={handleSubmitPayment}
      />

      <div className="flex justify-end border-color-border-input-lightmode pb-ds-2xl pt-ds-xl dark:border-color-border-input-darkmode sm:border-t">
        <Button
          variant="brand"
          size="lg"
          onClick={() => paymentFormRef.current?.triggerSubmit()}
          className="sm:w-fit"
          loading={isLoading}
        >
          Confirm and Pay
        </Button>
      </div>
    </div>
  );
};

export default ConfirmAndPay;

const PriceRow: React.FC<PriceRowProps> = ({ title, amount, brand = false, tooltip = null }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-1">
      <span
        className={classNames(
          brand
            ? 'typography-product-subheading  text-color-brand-primary'
            : 'typography-product-body-highlight',
        )}
      >
        {title}
      </span>
      {tooltip && (
        <>
          <InfoCircle
            className="ml-2 h-4 w-4 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
            data-tooltip-id={tooltip.id}
          />
          <Tooltip id={tooltip.id} content={tooltip.text} place="bottom" />
        </>
      )}
    </div>
    <span
      className={classNames(
        brand
          ? 'typography-product-subheading text-color-brand-primary'
          : 'typography-product-body-highlight',
      )}
    >
      {amount}
    </span>
  </div>
);
